<template>
  <component
    :is="as"
    ref="targetRef"
    :style="containerStyle"
    :data-reveal="direction"
    :data-visible="isVisible"
  >
    <slot />
  </component>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";

const props = defineProps({
  // Qué elemento renderizar (div, article, section, etc.)
  as: { type: String, default: "div" },

  // Dirección de entrada
  direction: {
    type: String,
    default: "up",
    validator: (v) => ["up", "down", "left", "right", "none"].includes(v),
  },

  // Distancia en px del desplazamiento inicial
  distance: { type: Number, default: 24 },

  // Duración de la animación en ms
  duration: { type: Number, default: 600 },

  // Delay en ms (útil para stagger en grillas: :delay="(index % 3) * 100")
  delay: { type: Number, default: 0 },

  // Threshold del IntersectionObserver (0-1)
  threshold: { type: Number, default: 0.15 },

  // Si animar solo la primera vez que aparece (true) o cada vez que entra/sale
  once: { type: Boolean, default: true },

  // Safety net: forzar visibilidad después de X ms si el observer no disparó
  safetyTimeout: { type: Number, default: 2000 },
});

const targetRef = ref(null);
const isVisible = ref(false);
let observer = null;
let safetyTimer = null;

onMounted(() => {
  // SSR o browser sin IntersectionObserver → mostrar de una
  if (!targetRef.value || typeof IntersectionObserver === "undefined") {
    isVisible.value = true;
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        isVisible.value = true;
        if (safetyTimer) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        if (props.once && observer) {
          observer.disconnect();
        }
      } else if (!props.once) {
        isVisible.value = false;
      }
    },
    { threshold: props.threshold },
  );

  observer.observe(targetRef.value);

  // Safety net: si después de N ms no entró al viewport, forzar visibilidad
  if (props.safetyTimeout > 0) {
    safetyTimer = setTimeout(() => {
      if (!isVisible.value) {
        // eslint-disable-next-line no-console
        console.warn(
          "[RevealOnScroll] Safety net: forzando visibilidad después de",
          props.safetyTimeout,
          "ms",
        );
        isVisible.value = true;
      }
    }, props.safetyTimeout);
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
});

const containerStyle = computed(() => ({
  "--reveal-duration": `${props.duration}ms`,
  "--reveal-delay": `${props.delay}ms`,
  "--reveal-distance": `${props.distance}px`,
}));
</script>

<!--
  NOTA: NO usamos <style scoped> a propósito.
  El elemento raíz es <component :is="as"> (dinámico), y el atributo
  data-v-xxx que agrega Vue para scope puede NO transferirse al elemento
  rendered real (div/article/etc). Sin scope, [data-reveal] matchea siempre.
  Los selectores son suficientemente específicos para no chocar con otros componentes.
-->
<style>
[data-reveal] {
  opacity: 0;
  transition:
    opacity var(--reveal-duration, 600ms) ease-out
      var(--reveal-delay, 0ms),
    transform var(--reveal-duration, 600ms) cubic-bezier(0.22, 1, 0.36, 1)
      var(--reveal-delay, 0ms);
  will-change: opacity, transform;
}

/* Cuándo se hace visible: opacity a 1 + reset del transform */
[data-reveal][data-visible="true"] {
  opacity: 1;
}

[data-reveal="up"] {
  transform: translateY(var(--reveal-distance, 24px));
}
[data-reveal="up"][data-visible="true"] {
  transform: translateY(0);
}

[data-reveal="down"] {
  transform: translateY(calc(-1 * var(--reveal-distance, 24px)));
}
[data-reveal="down"][data-visible="true"] {
  transform: translateY(0);
}

[data-reveal="left"] {
  transform: translateX(calc(-1 * var(--reveal-distance, 24px)));
}
[data-reveal="left"][data-visible="true"] {
  transform: translateX(0);
}

[data-reveal="right"] {
  transform: translateX(var(--reveal-distance, 24px));
}
[data-reveal="right"][data-visible="true"] {
  transform: translateX(0);
}

[data-reveal="none"] {
  transform: none;
}
[data-reveal="none"][data-visible="true"] {
  transform: none;
}

/* Accesibilidad: respetar prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  [data-reveal] {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
